// Utilizadores.js - VERSÃO COMPLETA COM DASHBOARD DO UTILIZADOR
// ✅ Modal "Ver Detalhes" com KPIs, gráficos, funil CRM e atividade
// 🔥 Endpoints: /users/...
// 🎨 FIX 3.9: corrigido perfil do loggedUser que vinha undefined
//             - obterUtilizadorLogado agora procura perfil em múltiplos locais
//             - podeAlternarEstado usa isSuperadmin do state como fallback
//             - debug detalhado para identificar porque o perfil não é lido
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Utilizadores.css';
import { Avatar, Icon } from './SharedComponents';
import {
    menuStructure,
    getPermissoesPorPerfil,
    combinarPermissoes,
    getModulosComPermissoes
} from '../utils/menuStructure';

const API_BASE_URL = '/api';

// 🔥 HELPER: normaliza o perfil
const normalizarPerfil = (user) => {
    if (!user) return '';
    const raw = user.perfil || user.role || user.tipo || '';
    return String(raw).toLowerCase().trim();
};

// 🔥 HELPER ULTRA-ROBUSTO v2: obtém o utilizador logado com perfil garantido
const obterUtilizadorLogado = (loggedUser) => {
    // 1) Se já temos um objeto com perfil válido, usar
    if (loggedUser && (loggedUser.perfil || loggedUser.role)) {
        const perfil = loggedUser.perfil || loggedUser.role;
        if (perfil && String(perfil).trim()) {
            return loggedUser;
        }
    }
    // 2) Se tem _id mas não tem perfil, tentar completar
    if (loggedUser && (loggedUser._id || loggedUser.id || loggedUser.userId)) {
        // Vai buscar o perfil de outra fonte
        try {
            const raw = localStorage.getItem('usuario');
            if (raw) {
                const stored = JSON.parse(raw);
                if (stored && (stored.perfil || stored.role)) {
                    return { ...loggedUser, perfil: stored.perfil || stored.role, empresaId: stored.empresaId || loggedUser.empresaId };
                }
            }
        } catch (e) { }
        return loggedUser;
    }
    // 3) Fallback: localStorage
    try {
        const raw = localStorage.getItem('usuario');
        if (raw) {
            const stored = JSON.parse(raw);
            if (stored && (stored._id || stored.id || stored.userId)) return stored;
            if (stored?.data && (stored.data._id || stored.data.id || stored.data.userId)) return stored.data;
            if (stored?.user && (stored.user._id || stored.user.id || stored.user.userId)) return stored.user;
            if (stored && (stored.perfil || stored.role)) return stored;
        }
    } catch (e) { }
    // 4) Fallback: token JWT
    try {
        const token = localStorage.getItem('token');
        if (token) {
            const parts = token.split('.');
            if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                const id = payload.id || payload.userId || payload.sub || payload._id;
                const perfil = payload.perfil || payload.role || payload.tipo;
                const empresaId = payload.empresaId || payload.empresa;
                if (id || perfil) return { _id: id, perfil, empresaId };
            }
        }
    } catch (e) { }
    return null;
};

// 🔥 HELPER: utilizador apagado (soft delete)
const utilizadorEstaApagado = (u) => {
    if (!u) return false;
    return (
        u.apagado === true ||
        u.deletado === true ||
        u.removido === true ||
        u.eliminado === true ||
        u.estado === 'apagado' ||
        u.status === 'deleted'
    );
};

// 🔥 HELPER: extrai ID de qualquer formato
const extrairId = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj.trim();
    const id = obj._id || obj.id || obj.userId || '';
    return String(id).trim();
};

// 🔥 HELPER: extrai perfil de múltiplos locais
const extrairPerfil = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj.toLowerCase().trim();
    const raw = obj.perfil || obj.role || obj.tipo || obj.userRole || '';
    return String(raw).toLowerCase().trim();
};

export default function Utilizadores() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('dados');
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        perfil: 'Normal',
        empresaId: '',
        empresaNome: '',
        ativo: true,
        senha: '',
        permissoesIndividuais: {}
    });
    const [tempToast, setTempToast] = useState(null);
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    // 🔥 NOVO: guarda o perfil e ID do loggedUser em state separado (nunca fica null após carregar)
    const [loggedUserPerfil, setLoggedUserPerfil] = useState('');
    const [loggedUserId, setLoggedUserId] = useState('');
    const [empresasList, setEmpresasList] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [permissoesBase, setPermissoesBase] = useState({});
    const [modulosAtivosEmpresa, setModulosAtivosEmpresa] = useState({});
    const [deletingUser, setDeletingUser] = useState(false);
    const [sessaoCarregada, setSessaoCarregada] = useState(false);

    // ==============================================
    // 🔥 DASHBOARD
    // ==============================================
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [userForDashboard, setUserForDashboard] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [dashboardErro, setDashboardErro] = useState(null);
    const [dashboardAno, setDashboardAno] = useState(new Date().getFullYear());
    const [dashboardDados, setDashboardDados] = useState(null);
    const [dashboardAba, setDashboardAba] = useState('visao');
    const [evolucaoPontoAtivo, setEvolucaoPontoAtivo] = useState(null);

    // ==============================================
    // IMAGEM DE PERFIL
    // ==============================================
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    // ==============================================
    // LIMITE DE UTILIZADORES
    // ==============================================
    const [limiteUtilizadores, setLimiteUtilizadores] = useState({ limite: 0, utilizadoresAtuais: 0, disponivel: 0 });
    const [loadingLimite, setLoadingLimite] = useState(false);

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(9);
    const [totalPages, setTotalPages] = useState(1);

    // ==============================================
    // ESTATÍSTICAS
    // ==============================================
    const stats = {
        total: users.length,
        ativos: users.filter(u => u.ativo !== false).length,
        inativos: users.filter(u => u.ativo === false).length,
        admins: users.filter(u => u.perfil === 'Admin').length
    };

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        if (user.empresa && typeof user.empresa === 'object' && user.empresa._id) return user.empresa._id;
        if (user.empresa && typeof user.empresa === 'object' && user.empresa.id) return user.empresa.id;
        return null;
    };

    const getImagemUrl = (imagemPerfil) => {
        if (!imagemPerfil) return null;
        if (imagemPerfil.startsWith('http://') || imagemPerfil.startsWith('https://')) return imagemPerfil;
        let cleanPath = imagemPerfil.replace(/^\/+/, '');
        if (!cleanPath.startsWith('uploads/')) cleanPath = `uploads/${cleanPath}`;
        return `/${cleanPath}`;
    };

    const showTempToast = (title, message, type = 'success') => {
        setTempToast({ title, message, type });
        setTimeout(() => setTempToast(null), 3500);
    };

    // ==============================================
    // 🔥 DASHBOARD — HELPERS
    // ==============================================
    const formatarMoedaDashboard = (valor) => {
        const cfg = dashboardDados?.moeda || { simbolo: 'MT', separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 };
        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${cfg.separadorDecimal}${'0'.repeat(cfg.casasDecimais)} ${cfg.simbolo}`;
        }
        let v = Number(valor).toFixed(cfg.casasDecimais).replace('.', cfg.separadorDecimal);
        const partes = v.split(cfg.separadorDecimal);
        partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, cfg.separadorMilhar);
        return `${partes.join(cfg.separadorDecimal)} ${cfg.simbolo}`;
    };

    const formatarNumeroAbreviado = (valor) => {
        if (valor === undefined || valor === null || isNaN(valor)) return '0';
        const n = Number(valor);
        if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
        return String(n);
    };

    const formatarDataDashboard = (data) => {
        if (!data) return '-';
        try {
            return new Date(data).toLocaleDateString('pt-PT', {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
        } catch (e) { return '-'; }
    };

    const formatarDataHoraDashboard = (data) => {
        if (!data) return '-';
        try {
            return new Date(data).toLocaleString('pt-PT', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch (e) { return '-'; }
    };

    // ==============================================
    // 🔥 DASHBOARD — ABRIR/FECHAR/CARREGAR
    // ==============================================
    const openDashboardModal = (user) => {
        setUserForDashboard(user);
        setShowDashboardModal(true);
        setDashboardAno(new Date().getFullYear());
        setDashboardAba('visao');
        setDashboardDados(null);
        setDashboardErro(null);
        setEvolucaoPontoAtivo(null);
        carregarDashboard(user._id, new Date().getFullYear());
    };

    const closeDashboardModal = () => {
        setShowDashboardModal(false);
        setUserForDashboard(null);
        setDashboardDados(null);
        setDashboardErro(null);
        setEvolucaoPontoAtivo(null);
    };

    const carregarDashboard = async (userId, ano) => {
        setDashboardLoading(true);
        setDashboardErro(null);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('Sessão expirada');

            const response = await axios.get(
                `${API_BASE_URL}/users/${userId}/dashboard?ano=${ano}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setDashboardDados(response.data.data);
            } else {
                throw new Error(response.data.erro || 'Erro ao carregar');
            }
        } catch (err) {
            console.error('❌ Erro dashboard:', err);
            setDashboardErro(err.response?.data?.erro || err.message || 'Erro ao carregar');
        } finally {
            setDashboardLoading(false);
        }
    };

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const getCurrentPageUsers = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
    };

    const goToPage = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        const tw = document.querySelector('.utilizadores-table-wrapper');
        if (tw) tw.scrollTop = 0;
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            const tw = document.querySelector('.utilizadores-table-wrapper');
            if (tw) tw.scrollTop = 0;
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            const tw = document.querySelector('.utilizadores-table-wrapper');
            if (tw) tw.scrollTop = 0;
        }
    };

    useEffect(() => {
        const total = Math.ceil(filteredUsers.length / itemsPerPage);
        setTotalPages(total > 0 ? total : 1);
        if (currentPage > total && total > 0) setCurrentPage(1);
    }, [filteredUsers, itemsPerPage]);

    useEffect(() => {
        if (searchTerm && searchTerm.includes('@') && searchTerm.includes('.')) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailPattern.test(searchTerm)) setSearchTerm('');
        }
    }, [searchTerm]);

    // ==============================================
    // MÓDULOS / PERMISSÕES
    // ==============================================
    const carregarModulosDaEmpresa = async (empresaId) => {
        if (!empresaId) { setModulosAtivosEmpresa({}); return; }
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`${API_BASE_URL}/empresas/${empresaId}/modulos`,
                { headers: { Authorization: `Bearer ${token}` } });
            if (response.data && response.data.success) {
                const modulos = response.data.data || {};
                setModulosAtivosEmpresa(modulos);
                return modulos;
            }
        } catch (err) { console.error('❌ Erro módulos:', err); }
        return {};
    };

    const filtrarPermissoesPorModulosAtivos = (permissoes, modulosAtivos) => {
        if (!modulosAtivos || Object.keys(modulosAtivos).length === 0) return permissoes;
        const resultado = {};
        Object.keys(permissoes).forEach(moduloId => {
            const moduloAtivo = modulosAtivos[moduloId]?.ativo === true;
            if (moduloAtivo) {
                resultado[moduloId] = {
                    ativo: permissoes[moduloId]?.ativo || false,
                    submodulos: {}
                };
                const subPerm = permissoes[moduloId]?.submodulos || {};
                const subEmp = modulosAtivos[moduloId]?.submodulos || {};
                Object.keys(subPerm).forEach(subId => {
                    if (subEmp[subId] === true) resultado[moduloId].submodulos[subId] = subPerm[subId];
                });
            }
        });
        return resultado;
    };

    const atualizarPermissoesBase = (perfil, empresaId = null) => {
        let permissoes = getPermissoesPorPerfil(perfil);
        if (empresaId) {
            const empresa = empresasList.find(e => e._id === empresaId);
            if (empresa) {
                carregarModulosDaEmpresa(empresaId).then(modulos => {
                    if (modulos && Object.keys(modulos).length > 0) {
                        permissoes = filtrarPermissoesPorModulosAtivos(permissoes, modulos);
                    }
                    setPermissoesBase(permissoes);
                    if (!formData.permissoesIndividuais || Object.keys(formData.permissoesIndividuais).length === 0) {
                        setFormData(prev => ({ ...prev, permissoesIndividuais: JSON.parse(JSON.stringify(permissoes)) }));
                    }
                });
                return;
            }
        }
        setPermissoesBase(permissoes);
        if (!formData.permissoesIndividuais || Object.keys(formData.permissoesIndividuais).length === 0) {
            setFormData(prev => ({ ...prev, permissoesIndividuais: JSON.parse(JSON.stringify(permissoes)) }));
        }
    };

    const togglePermissaoIndividual = (moduloId, submoduloId = null) => {
        setFormData(prev => {
            const novasPermissoes = JSON.parse(JSON.stringify(prev.permissoesIndividuais || {}));
            if (!novasPermissoes[moduloId]) {
                const base = permissoesBase[moduloId] || { ativo: false, submodulos: {} };
                novasPermissoes[moduloId] = { ativo: base.ativo || false, submodulos: { ...base.submodulos } };
            }
            if (submoduloId === null) {
                const novoEstado = !novasPermissoes[moduloId].ativo;
                novasPermissoes[moduloId].ativo = novoEstado;
                if (!novoEstado) {
                    Object.keys(novasPermissoes[moduloId].submodulos).forEach(subId => {
                        novasPermissoes[moduloId].submodulos[subId] = false;
                    });
                } else {
                    const base = permissoesBase[moduloId] || { submodulos: {} };
                    Object.keys(novasPermissoes[moduloId].submodulos).forEach(subId => {
                        if (base.submodulos[subId]) novasPermissoes[moduloId].submodulos[subId] = true;
                    });
                }
            } else {
                const novoEstado = !novasPermissoes[moduloId].submodulos[submoduloId];
                novasPermissoes[moduloId].submodulos[submoduloId] = novoEstado;
                let algumAtivo = false;
                Object.values(novasPermissoes[moduloId].submodulos).forEach(val => { if (val) algumAtivo = true; });
                novasPermissoes[moduloId].ativo = algumAtivo;
            }
            return { ...prev, permissoesIndividuais: novasPermissoes };
        });
    };

    const resetarPermissoesParaBase = () => {
        setFormData(prev => ({ ...prev, permissoesIndividuais: JSON.parse(JSON.stringify(permissoesBase)) }));
        showTempToast('Permissões', 'Permissões redefinidas para o padrão do perfil', 'success');
    };

    const temPermissoesIndividuais = () => {
        if (!formData.permissoesIndividuais) return false;
        return JSON.stringify(permissoesBase) !== JSON.stringify(formData.permissoesIndividuais);
    };

    const isModuloAtivoNaEmpresa = (moduloId) => {
        if (isSuperadmin) return true;
        if (!modulosAtivosEmpresa || Object.keys(modulosAtivosEmpresa).length === 0) return true;
        return modulosAtivosEmpresa[moduloId]?.ativo === true;
    };

    const isSubmoduloAtivoNaEmpresa = (moduloId, submoduloId) => {
        if (isSuperadmin) return true;
        if (!modulosAtivosEmpresa || Object.keys(modulosAtivosEmpresa).length === 0) return true;
        return modulosAtivosEmpresa[moduloId]?.submodulos?.[submoduloId] === true;
    };

    // ==============================================
    // LIMITE
    // ==============================================
    const carregarLimiteUtilizadores = async (empresaId) => {
        if (!empresaId) return;
        setLoadingLimite(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`${API_BASE_URL}/licencas/empresa/${empresaId}/limite-utilizadores`,
                { headers: { Authorization: `Bearer ${token}` } });
            setLimiteUtilizadores(response.data);
            return response.data;
        } catch (err) {
            setLimiteUtilizadores({ limite: 0, utilizadoresAtuais: 0, disponivel: 0 });
            return null;
        } finally { setLoadingLimite(false); }
    };

    const podeAdicionarUtilizador = () => {
        if (isSuperadmin) return true;
        if (limiteUtilizadores.limite === 0) return false;
        return limiteUtilizadores.disponivel > 0;
    };

    const getLimiteMessage = (limiteData) => {
        const dados = limiteData || limiteUtilizadores;
        if (isSuperadmin) return null;
        if (dados.limite === 0) return '❌ NÃO É POSSÍVEL ADICIONAR UTILIZADORES: Esta empresa não possui uma licença ativa.';
        if (dados.disponivel <= 0) return `❌ NÃO É POSSÍVEL ADICIONAR MAIS UTILIZADORES: A empresa atingiu o limite máximo de ${dados.limite} utilizador(es).`;
        return `✅ Disponível: ${dados.disponivel} vaga(s) para novos utilizadores (${dados.utilizadoresAtuais}/${dados.limite})`;
    };

    // ==============================================
    // NOTIFICAR / UPLOAD
    // ==============================================
    const notificarAtualizacaoUtilizadores = (empresaId, userId, action) => {
        window.dispatchEvent(new CustomEvent('utilizadoresAtualizados', {
            detail: { empresaId, userId, action: action || 'update', timestamp: Date.now() }
        }));
        if (action === 'addUser' || action === 'removeUser') {
            window.dispatchEvent(new CustomEvent('associacaoEmpresaUtilizadorAlterada', {
                detail: { empresaId, userId, action, timestamp: Date.now() }
            }));
        }
    };

    const uploadImage = async (userId, file) => {
        const fd = new FormData();
        fd.append('imagemPerfil', file);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token não encontrado');
        const response = await axios.put(`${API_BASE_URL}/users/${userId}/imagem`, fd, {
            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        });
        return response.data;
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showTempToast('Erro', 'Por favor, selecione uma imagem válida', 'danger');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                showTempToast('Erro', 'A imagem não pode exceder 5MB', 'danger');
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const removeSelectedImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ==============================================
    // LOGGED USER — agora define perfil e ID em state dedicado
    // ==============================================
    const aplicarLoggedUser = (userData) => {
        if (!userData) return;
        setLoggedUser(userData);
        const perfil = extrairPerfil(userData);
        const id = extrairId(userData);
        setLoggedUserPerfil(perfil);
        setLoggedUserId(id);
        setIsSuperadmin(perfil === 'superadmin');
        console.log('✅ [aplicarLoggedUser] Perfil:', perfil, '| ID:', id, '| userData:', userData);
    };

    const atualizarLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return null;
            try {
                const response = await axios.get(`${API_BASE_URL}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data && response.data.success) {
                    const userData = response.data.data;
                    const empresaId = obterEmpresaIdDoUtilizador(userData);
                    if (empresaId && userData.empresaId && typeof userData.empresaId === 'object' && userData.empresaId._id) {
                        userData.empresaId = userData.empresaId._id;
                    }
                    aplicarLoggedUser(userData);
                    localStorage.setItem('usuario', JSON.stringify(userData));
                    return userData;
                }
            } catch (apiErr) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    aplicarLoggedUser(storedUser);
                    return storedUser;
                }
            }
            return null;
        } catch (err) { return null; }
    };

    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && (storedUser._id || storedUser.perfil)) {
                    aplicarLoggedUser(storedUser);
                    return storedUser;
                }
                return null;
            }
            try {
                const response = await axios.get(`${API_BASE_URL}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data && response.data.success) {
                    const userData = response.data.data;
                    const empresaId = obterEmpresaIdDoUtilizador(userData);
                    if (empresaId && userData.empresaId && typeof userData.empresaId === 'object' && userData.empresaId._id) {
                        userData.empresaId = userData.empresaId._id;
                    }
                    aplicarLoggedUser(userData);
                    localStorage.setItem('usuario', JSON.stringify(userData));
                    return userData;
                }
            } catch (apiErr) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    aplicarLoggedUser(storedUser);
                    return storedUser;
                }
            }
            try {
                const tokenParts = token.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    if (payload && (payload.id || payload.userId || payload.sub)) {
                        const userId = payload.id || payload.userId || payload.sub;
                        const perfil = payload.perfil || payload.role || 'Normal';
                        const empresaId = payload.empresaId || payload.empresa || null;
                        const basicUser = { _id: userId, perfil, empresaId };
                        aplicarLoggedUser(basicUser);
                        return basicUser;
                    }
                }
            } catch (tokenErr) { }
            return null;
        } catch (err) { return null; }
    };

    // ==============================================
    // 🔥 FETCH USERS / EMPRESAS
    // ==============================================
    const fetchUsers = async (userOverride = null) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) { setLoading(false); return; }

            const response = await axios.get(`${API_BASE_URL}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                let allUsers = response.data.data || response.data;

                let user = userOverride || obterUtilizadorLogado(loggedUser);
                if (!user || !user._id) user = await fetchLoggedUser();

                const perfilLogado = normalizarPerfil(user);

                let filteredUsersList = [];
                if (perfilLogado === 'superadmin') {
                    filteredUsersList = allUsers;
                } else if (perfilLogado === 'admin') {
                    let empresaId = obterEmpresaIdDoUtilizador(user);
                    if (empresaId) {
                        const empresaIdStr = String(empresaId);
                        filteredUsersList = allUsers.filter(u => {
                            const userEmpresaId = obterEmpresaIdDoUtilizador(u);
                            const userEmpresaIdStr = userEmpresaId ? String(userEmpresaId) : null;
                            return userEmpresaIdStr === empresaIdStr && normalizarPerfil(u) !== 'superadmin';
                        });
                    } else {
                        filteredUsersList = allUsers.filter(u => u._id === user._id && normalizarPerfil(u) !== 'superadmin');
                    }
                } else {
                    filteredUsersList = allUsers.filter(u => u._id === user._id);
                }

                filteredUsersList = filteredUsersList.filter(u => !utilizadorEstaApagado(u));

                const sortedUsers = [...filteredUsersList].sort((a, b) => {
                    const dateA = new Date(a.dataCriacao || a.createdAt || 0);
                    const dateB = new Date(b.dataCriacao || b.createdAt || 0);
                    if (dateA.getTime() && dateB.getTime() && dateA.getTime() !== dateB.getTime()) return dateB - dateA;
                    if (a._id && b._id) return b._id.localeCompare(a._id);
                    return 0;
                });

                setUsers(sortedUsers);
                setFilteredUsers(sortedUsers);
                setCurrentPage(1);
            }
        } catch (err) {
            showTempToast('Erro', 'Não foi possível carregar os utilizadores', 'danger');
        } finally { setLoading(false); }
    };

    const fetchEmpresas = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`${API_BASE_URL}/empresas/options/dropdown`,
                { headers: { Authorization: `Bearer ${token}` } });
            if (response.data.success) setEmpresasList(response.data.data);
        } catch (err) { }
    };

    // ==============================================
    // FILTRO
    // ==============================================
    useEffect(() => {
        if (!searchTerm.trim()) { setFilteredUsers(users); setCurrentPage(1); return; }
        const term = searchTerm.toLowerCase().trim();
        const filtered = users.filter(u =>
            u.nome?.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term) ||
            u.empresaNome?.toLowerCase().includes(term) ||
            u.perfil?.toLowerCase().includes(term)
        );
        setFilteredUsers(filtered);
        setCurrentPage(1);
    }, [searchTerm, users]);

    // ==============================================
    // 🔥 EFEITOS INICIAIS
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            const user = await fetchLoggedUser();
            await fetchEmpresas();
            await fetchUsers(user);
            if (user) {
                aplicarLoggedUser(user);
                const perfilLogado = normalizarPerfil(user);
                if (perfilLogado === 'admin') {
                    const empresaId = obterEmpresaIdDoUtilizador(user);
                    if (empresaId) {
                        await carregarLimiteUtilizadores(empresaId);
                        await carregarModulosDaEmpresa(empresaId);
                    }
                }
            }
            setSessaoCarregada(true);
        };
        carregarDados();
        
    }, []);

    useEffect(() => {
        if (loggedUser && sessaoCarregada) {
            fetchUsers(loggedUser);
        }
        
    }, [loggedUser?._id, loggedUser?.perfil]);

    useEffect(() => {
        if (showModal && !isEditing && !isSuperadmin) {
            const currentUser = obterUtilizadorLogado(loggedUser);
            if (normalizarPerfil(currentUser) === 'admin') {
                const empresaId = obterEmpresaIdDoUtilizador(currentUser);
                if (empresaId) {
                    carregarLimiteUtilizadores(empresaId);
                    carregarModulosDaEmpresa(empresaId);
                }
            }
        }
        
    }, [showModal, isEditing, isSuperadmin, loggedUser]);

    useEffect(() => {
        if (formData.perfil) atualizarPermissoesBase(formData.perfil, formData.empresaId || null);
        
    }, [formData.perfil, formData.empresaId]);

    useEffect(() => {
        if (isSuperadmin && formData.empresaId) carregarModulosDaEmpresa(formData.empresaId);
        
    }, [formData.empresaId, isSuperadmin]);

    useEffect(() => {
        const h1 = () => { fetchUsers(loggedUser); fetchEmpresas(); };
        window.addEventListener('utilizadoresAtualizados', h1);
        window.addEventListener('associacaoEmpresaUtilizadorAlterada', h1);
        return () => {
            window.removeEventListener('utilizadoresAtualizados', h1);
            window.removeEventListener('associacaoEmpresaUtilizadorAlterada', h1);
        };
        
    }, [loggedUser]);

    // ==============================================
    // 🔥 PERMISSÕES DE AÇÃO — VERSÃO 3.9
    // Usam loggedUserPerfil e loggedUserId do state (sempre disponíveis após carregar)
    // ==============================================

    // 🔥 Helper: obtém o perfil atual do loggedUser (usa state dedicado como fallback)
    const getPerfilAtual = () => {
        // 1) State dedicado (mais fiável)
        if (loggedUserPerfil) return loggedUserPerfil;
        // 2) Direto do loggedUser
        const fromLogged = extrairPerfil(loggedUser);
        if (fromLogged) return fromLogged;
        // 3) Via obterUtilizadorLogado
        return extrairPerfil(obterUtilizadorLogado(loggedUser));
    };

    // 🔥 Helper: obtém o ID atual do loggedUser
    const getIdAtual = () => {
        if (loggedUserId) return loggedUserId;
        const fromLogged = extrairId(loggedUser);
        if (fromLogged) return fromLogged;
        return extrairId(obterUtilizadorLogado(loggedUser));
    };

    const podeEditar = (user) => {
        const perfilLogado = getPerfilAtual();
        if (!perfilLogado || !user) return false;

        const perfilAlvo = normalizarPerfil(user);

        if (perfilLogado === 'superadmin') return true;

        if (perfilLogado === 'admin') {
            if (perfilAlvo === 'superadmin') return false;
            if (String(user._id) === getIdAtual()) return false;
            if (perfilAlvo === 'admin') return false;

            const empresaAdmin = obterEmpresaIdDoUtilizador(loggedUser);
            const empresaUser = obterEmpresaIdDoUtilizador(user);
            if (!empresaAdmin) return false;
            if (!empresaUser) return true;
            return String(empresaAdmin) === String(empresaUser);
        }

        return String(user._id) === getIdAtual();
    };

    const podeApagar = (user) => {
        const perfilLogado = getPerfilAtual();
        if (!perfilLogado || !user) return false;

        const perfilAlvo = normalizarPerfil(user);

        if (perfilLogado === 'superadmin') return true;

        if (perfilLogado === 'admin') {
            if (perfilAlvo === 'superadmin') return false;
            if (String(user._id) === getIdAtual()) return false;
            if (perfilAlvo === 'admin') return false;

            const empresaAdmin = obterEmpresaIdDoUtilizador(loggedUser);
            const empresaUser = obterEmpresaIdDoUtilizador(user);
            if (!empresaAdmin) return false;
            if (!empresaUser) return true;
            return String(empresaAdmin) === String(empresaUser);
        }

        return String(user._id) === getIdAtual();
    };

    const podeApagarDefinitivamente = (user) => {
        if (!podeApagar(user)) return false;
        return user.ativo === false;
    };

    // 🔥 CORRIGIDO 3.9: usa getPerfilAtual() e getIdAtual() do state dedicado
    const podeAlternarEstado = (user) => {
        const perfilLogado = getPerfilAtual();
        const idLogado = getIdAtual();

        console.log('🔍 [podeAlternarEstado]', {
            userNome: user?.nome,
            userPerfil: user?.perfil,
            perfilLogado,
            idLogado,
            userIdAlvo: user?._id
        });

        if (!perfilLogado || !user) return false;

        const perfilAlvo = normalizarPerfil(user);
        const idAlvo = extrairId(user);

        // 🔥 SUPERADMIN — pode alternar todos exceto a si próprio
        if (perfilLogado === 'superadmin') {
            if (!idLogado) return true;
            if (!idAlvo) return true;
            return idLogado !== idAlvo;
        }

        // 🔥 ADMIN — pode alternar users da sua empresa
        if (perfilLogado === 'admin') {
            if (perfilAlvo === 'superadmin') return false;
            if (perfilAlvo === 'admin') return false;
            if (idLogado && idAlvo && idLogado === idAlvo) return false;

            const empresaAdmin = obterEmpresaIdDoUtilizador(loggedUser);
            const empresaUser = obterEmpresaIdDoUtilizador(user);

            if (!empresaAdmin) return false;
            if (!empresaUser) return true;
            return String(empresaAdmin) === String(empresaUser);
        }

        return false;
    };

    // ==============================================
    // ABRIR/FECHAR MODAL ADIÇÃO/EDIÇÃO
    // ==============================================
    const openAddModal = async () => {
        if (!isSuperadmin) {
            const currentUser = obterUtilizadorLogado(loggedUser);
            const empresaId = obterEmpresaIdDoUtilizador(currentUser);
            if (!empresaId) {
                showTempToast('Erro', 'Admin sem empresa associada. Contacte o Superadmin.', 'danger');
                return;
            }
            await carregarLimiteUtilizadores(empresaId);
            if (!podeAdicionarUtilizador()) {
                showTempToast('🚫 LIMITE ATINGIDO', getLimiteMessage() || 'Não é possível adicionar mais utilizadores.', 'warning');
                return;
            }
        }

        setSelectedUser(null);
        setFormData({
            nome: '', email: '', perfil: 'Normal', empresaId: '', empresaNome: '',
            ativo: true, senha: '', permissoesIndividuais: {}
        });
        setSelectedImage(null);
        setImagePreview(null);
        setActiveTab('dados');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsEditing(false);
        setShowModal(true);

        const permissoes = getPermissoesPorPerfil('Normal');
        setPermissoesBase(permissoes);
        setFormData(prev => ({ ...prev, permissoesIndividuais: JSON.parse(JSON.stringify(permissoes)) }));
    };

    const openEditModal = (user) => {
        if (!podeEditar(user)) {
            showTempToast('Erro', 'Não tem permissão para editar este utilizador', 'danger');
            return;
        }
        const currentUser = obterUtilizadorLogado(loggedUser);
        const isAdmin = normalizarPerfil(currentUser) === 'admin';

        let empresaId = obterEmpresaIdDoUtilizador(user) || '';
        let empresaNome = user.empresaNome || user.empresa || '';

        if (isAdmin) {
            const empresaAdminId = obterEmpresaIdDoUtilizador(currentUser);
            if (empresaAdminId) {
                empresaId = empresaAdminId;
                const empresa = empresasList.find(e => e._id === empresaAdminId);
                empresaNome = empresa?.nome || '';
            }
        }

        const permissoesIndividuais = user.permissoesIndividuais || {};
        const perfil = user.perfil || 'Normal';
        const permissoesBaseDoPerfil = getPermissoesPorPerfil(perfil);

        const permissoesCombinadas = Object.keys(permissoesBaseDoPerfil).reduce((acc, moduloId) => {
            acc[moduloId] = {
                ativo: permissoesIndividuais[moduloId]?.ativo !== undefined
                    ? permissoesIndividuais[moduloId].ativo
                    : permissoesBaseDoPerfil[moduloId].ativo,
                submodulos: {}
            };
            Object.keys(permissoesBaseDoPerfil[moduloId].submodulos).forEach(subId => {
                acc[moduloId].submodulos[subId] = permissoesIndividuais[moduloId]?.submodulos?.[subId] !== undefined
                    ? permissoesIndividuais[moduloId].submodulos[subId]
                    : permissoesBaseDoPerfil[moduloId].submodulos[subId];
            });
            return acc;
        }, {});

        setSelectedUser(user);
        setFormData({
            nome: user.nome || '', email: user.email || '', perfil,
            empresaId, empresaNome,
            ativo: user.ativo !== false, senha: '',
            permissoesIndividuais: permissoesCombinadas
        });
        setPermissoesBase(permissoesBaseDoPerfil);
        setSelectedImage(null);
        setImagePreview(null);
        setActiveTab('dados');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsEditing(true);
        setShowModal(true);
        if (empresaId) carregarModulosDaEmpresa(empresaId);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setIsEditing(false);
        setFormData({
            nome: '', email: '', perfil: 'Normal', empresaId: '', empresaNome: '',
            ativo: true, senha: '', permissoesIndividuais: {}
        });
        setSelectedImage(null);
        setImagePreview(null);
        setActiveTab('dados');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setModulosAtivosEmpresa({});
    };

    // ==============================================
    // SALVAR
    // ==============================================
    const saveUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) { showTempToast('Erro', 'Sessão expirada', 'danger'); return; }

            const currentUser = obterUtilizadorLogado(loggedUser);
            const perfilLogado = normalizarPerfil(currentUser);
            const isAdmin = perfilLogado === 'admin';
            const isSuper = perfilLogado === 'superadmin';

            if (!formData.nome.trim()) { showTempToast('Erro', 'O nome é obrigatório', 'danger'); return; }
            if (!formData.email.trim()) { showTempToast('Erro', 'O email é obrigatório', 'danger'); return; }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { showTempToast('Erro', 'Email inválido', 'danger'); return; }
            if (!isEditing && (!formData.senha || formData.senha.length < 6)) {
                showTempToast('Erro', 'A senha deve ter no mínimo 6 caracteres', 'danger'); return;
            }
            if (isAdmin && formData.perfil === 'Superadmin') {
                showTempToast('Erro', 'Admin não pode criar/editar Superadmin', 'danger'); return;
            }
            if (isAdmin) {
                const perfisPermitidos = ['Normal', 'Contabilista', 'Comercial'];
                if (!perfisPermitidos.includes(formData.perfil)) {
                    showTempToast('Erro', 'Admin só pode atribuir perfis: Normal, Contabilista ou Comercial', 'danger'); return;
                }
            }

            let empresaDestinoId = null;
            let empresaDestinoNome = null;

            if (isAdmin) {
                const empresaAdminId = obterEmpresaIdDoUtilizador(currentUser);
                if (!empresaAdminId) {
                    showTempToast('Erro', 'Admin sem empresa associada. Contacte o Superadmin.', 'danger'); return;
                }
                empresaDestinoId = empresaAdminId;
                const empresa = empresasList.find(e => e._id === empresaAdminId);
                empresaDestinoNome = empresa?.nome || null;
                if (isEditing && selectedUser) {
                    const empresaOriginal = obterEmpresaIdDoUtilizador(selectedUser);
                    if (empresaOriginal && String(empresaOriginal) !== String(empresaAdminId)) {
                        showTempToast('Erro', 'Admin não pode alterar a empresa de um utilizador.', 'danger'); return;
                    }
                }
            } else {
                empresaDestinoId = formData.empresaId || null;
                const empresa = empresasList.find(e => e._id === empresaDestinoId);
                empresaDestinoNome = empresa?.nome || null;
            }

            if (isSuper && empresaDestinoId) {
                const validacao = await validarLimiteEmpresa(empresaDestinoId, isEditing);
                if (!validacao.valido) { showTempToast('🚫 LIMITE DA EMPRESA', validacao.mensagem, 'danger'); return; }
            }

            if (!isSuper && !isEditing) {
                if (empresaDestinoId) await carregarLimiteUtilizadores(empresaDestinoId);
                if (limiteUtilizadores.limite === 0) { showTempToast('🚫 LICENÇA INATIVA', getLimiteMessage(), 'danger'); return; }
                if (limiteUtilizadores.disponivel <= 0) { showTempToast('🚫 LIMITE ATINGIDO', getLimiteMessage(), 'danger'); return; }
            }

            const updateData = {
                nome: formData.nome,
                email: formData.email.toLowerCase(),
                perfil: formData.perfil,
                ativo: formData.ativo,
                permissoesIndividuais: formData.permissoesIndividuais || {}
            };

            if (empresaDestinoId) {
                updateData.empresaId = empresaDestinoId;
                updateData.empresa = empresaDestinoNome || formData.empresaNome || '';
                updateData.empresaNome = empresaDestinoNome || formData.empresaNome || '';
            } else {
                updateData.empresaId = null;
                updateData.empresa = null;
                updateData.empresaNome = null;
            }

            if (formData.senha && formData.senha.length >= 6) updateData.senha = formData.senha;

            let userId = null;

            if (isEditing && selectedUser) {
                if (!podeEditar(selectedUser)) {
                    showTempToast('Erro', 'Não tem permissão para editar este utilizador', 'danger'); return;
                }
                userId = selectedUser._id;
                if (!updateData.senha) delete updateData.senha;
                await axios.put(`${API_BASE_URL}/users/${userId}`, updateData,
                    { headers: { Authorization: `Bearer ${token}` } });
                showTempToast('Sucesso', 'Utilizador atualizado com sucesso!', 'success');
            } else {
                const response = await axios.post(`${API_BASE_URL}/auth/register`, updateData,
                    { headers: { Authorization: `Bearer ${token}` } });
                userId = response.data.user?._id || response.data._id || response.data.user?.id;
                showTempToast('Sucesso', 'Utilizador criado com sucesso!', 'success');
            }

            if (userId && empresaDestinoId) {
                try {
                    const checkResponse = await axios.get(
                        `${API_BASE_URL}/empresas/${empresaDestinoId}/utilizadores`,
                        { headers: { Authorization: `Bearer ${token}` } });
                    const jaAssociado = checkResponse.data.data?.some(u => u._id === userId || u.userId === userId);
                    if (!jaAssociado) {
                        await axios.post(
                            `${API_BASE_URL}/empresas/${empresaDestinoId}/utilizadores`,
                            { userId },
                            { headers: { Authorization: `Bearer ${token}` } });
                    }
                } catch (err) { }
            }

            if (selectedImage && userId) {
                setIsUploadingImage(true);
                try {
                    await uploadImage(userId, selectedImage);
                    showTempToast('Sucesso', 'Imagem de perfil enviada com sucesso!', 'success');
                } catch (err) {
                    showTempToast('Aviso', 'Utilizador salvo, mas falha ao enviar imagem', 'warning');
                } finally { setIsUploadingImage(false); }
            }

            if (empresaDestinoId) notificarAtualizacaoUtilizadores(empresaDestinoId, userId, isEditing ? 'update' : 'addUser');

            closeModal();
            await fetchUsers(loggedUser);
            await atualizarLoggedUser();
            if (isAdmin && empresaDestinoId) await carregarLimiteUtilizadores(empresaDestinoId);

        } catch (err) {
            const msg = err.response?.data?.erro || err.response?.data?.message || 'Erro ao salvar utilizador';
            showTempToast('Erro', msg, 'danger');
        }
    };

    const validarLimiteEmpresa = async (empresaId, isEdit = false) => {
        if (!empresaId) return { valido: true, mensagem: '' };
        try {
            const token = localStorage.getItem('token');
            if (!token) return { valido: true, mensagem: '' };
            const response = await axios.get(`${API_BASE_URL}/licencas/empresa/${empresaId}/limite-utilizadores`,
                { headers: { Authorization: `Bearer ${token}` } });
            const dados = response.data;
            if (dados.limite === 0) return { valido: false, mensagem: '❌ NÃO É POSSÍVEL ADICIONAR/EDITAR: Esta empresa não possui uma licença ativa.' };
            if (isEdit && selectedUser) {
                const empresaAtual = obterEmpresaIdDoUtilizador(selectedUser);
                if (empresaAtual && String(empresaAtual) === String(empresaId)) return { valido: true, mensagem: '' };
            }
            if (dados.disponivel <= 0) return { valido: false, mensagem: `❌ A empresa atingiu o limite máximo de ${dados.limite} utilizador(es).` };
            return { valido: true, mensagem: '' };
        } catch (err) {
            if (err.response && err.response.status === 404) {
                return { valido: false, mensagem: '❌ Esta empresa não possui uma licença ativa.' };
            }
            return { valido: false, mensagem: 'Erro ao verificar o limite da empresa.' };
        }
    };

    // ==============================================
    // 🔥 APAGAR (só depois de desabilitar)
    // ==============================================
    const confirmDelete = (user) => {
        if (!podeApagar(user)) {
            showTempToast('Erro', 'Não tem permissão para apagar este utilizador', 'danger');
            return;
        }

        if (user.ativo !== false) {
            showTempToast(
                '🔒 Utilizador Ativo',
                `Desabilite o utilizador "${user.nome}" primeiro (botão verde/laranja), depois clique em apagar.`,
                'warning'
            );
            return;
        }

        setUserToDelete(user);
        setShowDeleteConfirm(true);
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setUserToDelete(null);
    };

    const deleteUser = async () => {
        if (!userToDelete) return;

        if (userToDelete.ativo !== false) {
            showTempToast('🔒 Utilizador Ativo', 'Desabilite o utilizador primeiro antes de apagar.', 'warning');
            setShowDeleteConfirm(false);
            return;
        }

        setDeletingUser(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada', 'danger');
                setDeletingUser(false);
                return;
            }

            await axios.delete(`${API_BASE_URL}/users/${userToDelete._id}`,
                { headers: { Authorization: `Bearer ${token}` } });

            setUsers(prev => prev.filter(u => String(u._id) !== String(userToDelete._id)));
            setFilteredUsers(prev => prev.filter(u => String(u._id) !== String(userToDelete._id)));

            const currentUser = obterUtilizadorLogado(loggedUser);
            let empresaId = obterEmpresaIdDoUtilizador(userToDelete);
            if (normalizarPerfil(currentUser) === 'admin') {
                const empresaAdminId = obterEmpresaIdDoUtilizador(currentUser);
                if (empresaAdminId) empresaId = empresaAdminId;
            }
            if (empresaId) notificarAtualizacaoUtilizadores(empresaId, userToDelete._id, 'removeUser');

            showTempToast('Sucesso', `Utilizador "${userToDelete.nome}" apagado definitivamente!`, 'success');
            setShowDeleteConfirm(false);
            setUserToDelete(null);

            await fetchUsers(loggedUser);
            await atualizarLoggedUser();

            if (normalizarPerfil(currentUser) === 'admin') {
                const empresaIdAdmin = obterEmpresaIdDoUtilizador(currentUser);
                if (empresaIdAdmin) await carregarLimiteUtilizadores(empresaIdAdmin);
            }
        } catch (err) {
            const msg = err.response?.data?.erro
                || err.response?.data?.message
                || 'Erro ao apagar utilizador';
            showTempToast('Erro', msg, 'danger');
        } finally {
            setDeletingUser(false);
        }
    };

    // ==============================================
    // TOGGLE (ativar/desativar)
    // ==============================================
    const toggleUserStatus = async (user) => {
        try {
            if (!podeAlternarEstado(user)) {
                showTempToast('Erro', 'Não tem permissão para alterar o estado deste utilizador', 'danger');
                return;
            }
            const token = localStorage.getItem('token');
            if (!token) { showTempToast('Erro', 'Sessão expirada', 'danger'); return; }

            await axios.patch(`${API_BASE_URL}/users/${user._id}/toggle`, {},
                { headers: { Authorization: `Bearer ${token}` } });

            showTempToast('Sucesso', `Utilizador ${user.nome} ${user.ativo ? 'desativado' : 'ativado'} com sucesso!`, 'success');
            await fetchUsers(loggedUser);
            await atualizarLoggedUser();

            const currentUser = obterUtilizadorLogado(loggedUser);
            if (normalizarPerfil(currentUser) === 'admin') {
                const empresaId = obterEmpresaIdDoUtilizador(currentUser);
                if (empresaId) await carregarLimiteUtilizadores(empresaId);
            }
        } catch (err) {
            const msg = err.response?.data?.erro
                || err.response?.data?.message
                || 'Erro ao alternar estado';
            showTempToast('Erro', msg, 'danger');
        }
    };

    // ==============================================
    // 🔥 DASHBOARD — RENDER GRÁFICOS
    // ==============================================
    const renderGraficoEvolucao = () => {
        if (!dashboardDados?.evolucao?.length) return null;

        const W = 800;
        const H = 340;
        const PAD_L = 55;
        const PAD_R = 30;
        const PAD_T = 55;
        const PAD_B = 30;

        const innerW = W - PAD_L - PAD_R;
        const innerH = H - PAD_T - PAD_B;

        const dados = dashboardDados.evolucao;
        const n = dados.length;

        const maxBruto = Math.max(
            ...dados.map(e => Math.max(
                e.cotacoes || 0,
                e.faturas || 0,
                e.recibos || 0,
                e.guias || 0
            )),
            1
        );
        const passoEscala = maxBruto <= 10 ? 2 : maxBruto <= 50 ? 5 : 10;
        const maxY = Math.ceil(maxBruto / passoEscala) * passoEscala;

        const xDe = (i) => PAD_L + (n > 1 ? (i * innerW) / (n - 1) : innerW / 2);
        const yDe = (v) => PAD_T + innerH - ((v || 0) / maxY) * innerH;

        const pontos = (chave) =>
            dados.map((e, i) => `${xDe(i)},${yDe(e[chave])}`).join(' ');

        const totais = {
            cotacoes: dados.reduce((s, e) => s + (e.cotacoes || 0), 0),
            faturas: dados.reduce((s, e) => s + (e.faturas || 0), 0),
            recibos: dados.reduce((s, e) => s + (e.recibos || 0), 0),
            guias: dados.reduce((s, e) => s + (e.guias || 0), 0)
        };

        const niveisY = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(maxY * p));

        const cores = {
            cotacoes: '#4f46e5',
            faturas: '#10b981',
            recibos: '#f59e0b',
            guias: '#8b5cf6'
        };

        const Y_NUMEROS = 22;

        return (
            <div className="ud-chart-wrapper ud-chart-wrapper-evolucao">
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    className="ud-chart-svg"
                    preserveAspectRatio="xMidYMid meet"
                    overflow="visible"
                >
                    <defs>
                        <linearGradient id="grad-cotacoes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
                            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
                        </linearGradient>
                    </defs>

                    <line
                        x1={PAD_L} y1={Y_NUMEROS + 10}
                        x2={W - PAD_R} y2={Y_NUMEROS + 10}
                        stroke="rgba(79, 70, 229, 0.15)"
                        strokeWidth="1"
                        strokeDasharray="3 3"
                    />

                    {dados.map((e, i) => {
                        const x = xDe(i);
                        const valor = e.cotacoes || 0;
                        const ativo = evolucaoPontoAtivo === i;
                        return (
                            <g
                                key={`num-${i}`}
                                onMouseEnter={() => setEvolucaoPontoAtivo(i)}
                                onMouseLeave={() => setEvolucaoPontoAtivo(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                <rect
                                    x={x - 14}
                                    y={Y_NUMEROS - 9}
                                    width="28"
                                    height="18"
                                    rx="9"
                                    fill={ativo ? cores.cotacoes : 'rgba(79, 70, 229, 0.10)'}
                                    style={{ transition: 'all 0.2s ease' }}
                                />
                                <text
                                    x={x}
                                    y={Y_NUMEROS + 3}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fontWeight="700"
                                    fill={ativo ? '#fff' : cores.cotacoes}
                                    style={{ transition: 'all 0.2s ease' }}
                                >
                                    {valor}
                                </text>
                            </g>
                        );
                    })}

                    {niveisY.map((val, idx) => {
                        const y = yDe(val);
                        return (
                            <g key={`y-${idx}`}>
                                <line
                                    x1={PAD_L} y1={y}
                                    x2={W - PAD_R} y2={y}
                                    stroke="rgba(0,0,0,0.07)"
                                    strokeWidth="1"
                                    strokeDasharray="4 4"
                                />
                                <text
                                    x={PAD_L - 8}
                                    y={y + 4}
                                    textAnchor="end"
                                    fontSize="11"
                                    fill="#9ca3af"
                                    fontWeight="600"
                                >
                                    {val}
                                </text>
                            </g>
                        );
                    })}

                    <polygon
                        points={`${PAD_L},${yDe(0)} ${pontos('cotacoes')} ${xDe(n - 1)},${yDe(0)}`}
                        fill="url(#grad-cotacoes)"
                        opacity="0.5"
                    />

                    <polyline points={pontos('cotacoes')} fill="none" stroke={cores.cotacoes}
                        strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    <polyline points={pontos('faturas')} fill="none" stroke={cores.faturas}
                        strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    <polyline points={pontos('recibos')} fill="none" stroke={cores.recibos}
                        strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    <polyline points={pontos('guias')} fill="none" stroke={cores.guias}
                        strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

                    {dados.map((e, i) => {
                        const x = xDe(i);
                        const ativo = evolucaoPontoAtivo === i;
                        return (
                            <g
                                key={`pts-${i}`}
                                onMouseEnter={() => setEvolucaoPontoAtivo(i)}
                                onMouseLeave={() => setEvolucaoPontoAtivo(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                <rect x={x - 15} y={PAD_T} width="30" height={innerH} fill="transparent" />
                                {['cotacoes', 'faturas', 'recibos', 'guias'].map((chave) => {
                                    const v = e[chave] || 0;
                                    if (v === 0) return null;
                                    return (
                                        <circle
                                            key={chave}
                                            cx={x} cy={yDe(v)}
                                            r={ativo ? 5.5 : 4}
                                            fill={cores[chave]}
                                            stroke="#fff"
                                            strokeWidth="1.5"
                                            style={{ transition: 'all 0.2s ease' }}
                                        />
                                    );
                                })}
                            </g>
                        );
                    })}

                    {evolucaoPontoAtivo !== null && (() => {
                        const e = dados[evolucaoPontoAtivo];
                        if (!e) return null;
                        const x = xDe(evolucaoPontoAtivo);

                        const ttW = 140;
                        const ttH = 100;
                        let ttX = x + 15;
                        if (ttX + ttW > W - PAD_R) ttX = x - ttW - 15;
                        let ttY = PAD_T + 10;

                        return (
                            <g style={{ pointerEvents: 'none' }}>
                                <rect x={ttX} y={ttY} width={ttW} height={ttH} rx="8"
                                    fill="rgba(28,28,30,0.96)" stroke="rgba(255,255,255,0.08)" />
                                <text x={ttX + 12} y={ttY + 20} fontSize="12" fill="#fff" fontWeight="700">
                                    {e.mes}
                                </text>
                                <circle cx={ttX + 14} cy={ttY + 38} r="4" fill={cores.cotacoes} />
                                <text x={ttX + 26} y={ttY + 42} fontSize="11" fill="#e5e7eb">
                                    Cotações: <tspan fontWeight="700" fill="#a5b4fc">{e.cotacoes || 0}</tspan>
                                </text>
                                <circle cx={ttX + 14} cy={ttY + 56} r="4" fill={cores.faturas} />
                                <text x={ttX + 26} y={ttY + 60} fontSize="11" fill="#e5e7eb">
                                    Faturas: <tspan fontWeight="700" fill="#6ee7b7">{e.faturas || 0}</tspan>
                                </text>
                                <circle cx={ttX + 14} cy={ttY + 74} r="4" fill={cores.recibos} />
                                <text x={ttX + 26} y={ttY + 78} fontSize="11" fill="#e5e7eb">
                                    Recibos: <tspan fontWeight="700" fill="#fbbf24">{e.recibos || 0}</tspan>
                                </text>
                                <circle cx={ttX + 14} cy={ttY + 92} r="4" fill={cores.guias} />
                                <text x={ttX + 26} y={ttY + 96} fontSize="11" fill="#e5e7eb">
                                    Guias: <tspan fontWeight="700" fill="#c4b5fd">{e.guias || 0}</tspan>
                                </text>
                            </g>
                        );
                    })()}
                </svg>

                <div className="ud-chart-labels">
                    {dados.map((e, i) => (
                        <span
                            key={i}
                            className={`ud-chart-label ${evolucaoPontoAtivo === i ? 'active' : ''}`}
                            onMouseEnter={() => setEvolucaoPontoAtivo(i)}
                            onMouseLeave={() => setEvolucaoPontoAtivo(null)}
                        >
                            {e.mes}
                        </span>
                    ))}
                </div>

                <div className="ud-chart-legenda">
                    <span>
                        <span className="dot" style={{ background: cores.cotacoes }} />
                        Cotações
                        <strong className="legenda-total">{totais.cotacoes}</strong>
                    </span>
                    <span>
                        <span className="dot" style={{ background: cores.faturas }} />
                        Faturas
                        <strong className="legenda-total">{totais.faturas}</strong>
                    </span>
                    <span>
                        <span className="dot" style={{ background: cores.recibos }} />
                        Recibos
                        <strong className="legenda-total">{totais.recibos}</strong>
                    </span>
                    <span>
                        <span className="dot" style={{ background: cores.guias }} />
                        Guias
                        <strong className="legenda-total">{totais.guias}</strong>
                    </span>
                </div>
            </div>
        );
    };

    const renderGraficoValor = () => {
        if (!dashboardDados?.evolucao?.length) return null;

        const maxValor = Math.max(
            ...dashboardDados.evolucao.map(e => Math.max(e.valorFaturado || 0, e.valorRecebido || 0)),
            1
        );

        return (
            <div className="ud-bars-wrapper">
                <div className="ud-bars">
                    {dashboardDados.evolucao.map((e, i) => {
                        const hFat = (e.valorFaturado / maxValor) * 100;
                        const hRec = (e.valorRecebido / maxValor) * 100;
                        return (
                            <div key={i} className="ud-bar-group">
                                <div className="ud-bar-pair">
                                    <div
                                        className="ud-bar ud-bar-fat"
                                        style={{ height: `${Math.max(hFat, 2)}%` }}
                                        data-valor={formatarMoedaDashboard(e.valorFaturado)}
                                    />
                                    <div
                                        className="ud-bar ud-bar-rec"
                                        style={{ height: `${Math.max(hRec, 2)}%` }}
                                        data-valor={formatarMoedaDashboard(e.valorRecebido)}
                                    />
                                </div>
                                <span className="ud-bar-label">{e.mes}</span>
                            </div>
                        );
                    })}
                </div>
                <div className="ud-chart-legenda" style={{ marginTop: '0.75rem' }}>
                    <span><span className="dot" style={{ background: '#10b981' }} /> Faturado</span>
                    <span><span className="dot" style={{ background: '#f59e0b' }} /> Recebido</span>
                </div>
            </div>
        );
    };

    const renderPizza = () => {
        const dist = dashboardDados?.distribuicao || [];
        if (!dist.length) return null;

        const total = dist.reduce((s, d) => s + d.valor, 0) || 1;
        let acumulado = 0;
        const raio = 45, cx = 50, cy = 50;
        const circunferencia = 2 * Math.PI * raio;

        return (
            <div className="ud-pizza-wrapper">
                <svg
                    viewBox="0 0 100 100"
                    className="ud-pizza-svg"
                    preserveAspectRatio="xMidYMid meet"
                    overflow="visible"
                >
                    {dist.map((d, i) => {
                        const percentual = (d.valor / total) * 100;
                        const strokeDasharray = `${(percentual / 100) * circunferencia} ${circunferencia}`;
                        const strokeDashoffset = -((acumulado / 100) * circunferencia);
                        acumulado += percentual;
                        return (
                            <circle
                                key={i}
                                cx={cx} cy={cy} r={raio}
                                fill="transparent"
                                stroke={d.cor}
                                strokeWidth="18"
                                strokeDasharray={strokeDasharray}
                                strokeDashoffset={strokeDashoffset}
                                transform={`rotate(-90 ${cx} ${cy})`}
                            />
                        );
                    })}
                    <circle cx={cx} cy={cy} r="30" fill="white" />
                    <text x={cx} y={cy - 1} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1a1a2e">
                        {total}
                    </text>
                    <text x={cx} y={cy + 7} textAnchor="middle" fontSize="4" fill="#6b7280" fontWeight="600">
                        TOTAL
                    </text>
                </svg>
                <div className="ud-pizza-legenda">
                    {dist.map((d, i) => (
                        <div key={i} className="ud-pizza-item">
                            <span className="dot" style={{ background: d.cor }}></span>
                            <span className="label">{d.modulo}</span>
                            <span className="valor">{d.valor}</span>
                            <span className="percent">{((d.valor / total) * 100).toFixed(0)}%</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderFunil = () => {
        const f = dashboardDados?.funil;
        if (!f) return null;

        const etapas = [
            { label: 'Propostas Feitas', descricao: 'Orçamentos enviados aos clientes', valor: f.propostas || 0, cor: '#4f46e5', corFim: '#6366f1', icone: 'description' },
            { label: 'Aprovadas', descricao: 'Propostas aceites pelo cliente', valor: f.aprovadas || 0, cor: '#06b6d4', corFim: '#22d3ee', icone: 'check_circle' },
            { label: 'Faturadas', descricao: 'Propostas convertidas em fatura', valor: f.faturadas || 0, cor: '#10b981', corFim: '#34d399', icone: 'receipt_long' },
            { label: 'Pagas', descricao: 'Faturas com pagamento confirmado', valor: f.pagas || 0, cor: '#f59e0b', corFim: '#fbbf24', icone: 'payments' },
            { label: 'Entregues', descricao: 'Guias de entrega emitidas', valor: f.entregues || 0, cor: '#8b5cf6', corFim: '#a78bfa', icone: 'local_shipping' }
        ];

        const maxValor = Math.max(...etapas.map(e => e.valor), 1);
        const primeira = etapas[0].valor || 1;

        const calcularTaxa = (i) => {
            if (i === 0) return null;
            const anterior = etapas[i - 1].valor;
            const atual = etapas[i].valor;
            if (anterior === 0) return 0;
            return Math.round((atual / anterior) * 100);
        };

        return (
            <div className="ud-funil-modern">
                <div className="ud-funil-modern-header">
                    <div className="ud-funil-modern-title">
                        <h3>Funil de Conversão</h3>
                        <p>Do orçamento à entrega — como as propostas evoluem</p>
                    </div>
                    <div className="ud-funil-modern-total">
                        <span className="total-label">Total Geral</span>
                        <span className="total-valor">{etapas[0].valor}</span>
                    </div>
                </div>

                <div className="ud-funil-modern-etapas">
                    {etapas.map((etapa, i) => {
                        const largura = (etapa.valor / maxValor) * 100;
                        const taxa = calcularTaxa(i);
                        const taxaGlobal = primeira > 0 ? Math.round((etapa.valor / primeira) * 100) : 0;

                        return (
                            <div key={i} className="ud-funil-modern-item">
                                <div className="ud-funil-modern-icone-wrapper">
                                    <div
                                        className="ud-funil-modern-icone"
                                        style={{
                                            background: `linear-gradient(135deg, ${etapa.cor} 0%, ${etapa.corFim} 100%)`,
                                            boxShadow: `0 4px 12px ${etapa.cor}40`
                                        }}
                                    >
                                        <Icon name={etapa.icone} size={18} color="#fff" />
                                    </div>
                                    {i < etapas.length - 1 && (<div className="ud-funil-modern-linha" />)}
                                </div>

                                <div className="ud-funil-modern-conteudo">
                                    <div className="ud-funil-modern-info">
                                        <div className="ud-funil-modern-info-text">
                                            <span className="ud-funil-modern-nome">{etapa.label}</span>
                                            <span className="ud-funil-modern-desc">{etapa.descricao}</span>
                                        </div>
                                        <div className="ud-funil-modern-valores">
                                            <span className="ud-funil-modern-valor">{etapa.valor}</span>
                                            <span className="ud-funil-modern-percent-global">{taxaGlobal}% do total</span>
                                        </div>
                                    </div>

                                    <div className="ud-funil-modern-barra-wrapper">
                                        <div
                                            className="ud-funil-modern-barra"
                                            style={{
                                                width: `${Math.max(largura, 3)}%`,
                                                background: `linear-gradient(90deg, ${etapa.cor} 0%, ${etapa.corFim} 100%)`,
                                                boxShadow: `0 2px 8px ${etapa.cor}30`
                                            }}
                                        >
                                            {etapa.valor > 0 && largura > 15 && (
                                                <span className="ud-funil-modern-barra-valor">{etapa.valor}</span>
                                            )}
                                        </div>
                                    </div>

                                    {taxa !== null && (
                                        <div className="ud-funil-modern-taxa-wrapper">
                                            <span className={`ud-funil-modern-taxa ${taxa >= 70 ? 'alta' : taxa >= 40 ? 'media' : 'baixa'}`}>
                                                <span className="ud-funil-modern-taxa-seta">
                                                    {taxa >= 70 ? '↑' : taxa >= 40 ? '→' : '↓'}
                                                </span>
                                                <span className="ud-funil-modern-taxa-label">Taxa de conversão</span>
                                                <span className="ud-funil-modern-taxa-valor">{taxa}%</span>
                                            </span>
                                            <span className="ud-funil-modern-taxa-detalhe">
                                                {etapas[i - 1].valor} → {etapa.valor}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="ud-funil-modern-resumo">
                    <div className="ud-funil-modern-resumo-card resumo-1">
                        <div className="resumo-icon"><Icon name="trending_up" size={20} color="#fff" /></div>
                        <div className="resumo-info">
                            <span className="resumo-label">Conversão Global</span>
                            <span className="resumo-valor">
                                {primeira > 0 ? Math.round((etapas[etapas.length - 1].valor / primeira) * 100) : 0}%
                            </span>
                            <span className="resumo-desc">{etapas[0].valor} → {etapas[etapas.length - 1].valor}</span>
                        </div>
                    </div>

                    <div className="ud-funil-modern-resumo-card resumo-2">
                        <div className="resumo-icon"><Icon name="description" size={20} color="#fff" /></div>
                        <div className="resumo-info">
                            <span className="resumo-label">Cotação → Fatura</span>
                            <span className="resumo-valor">{f.taxaConversaoCotacaoFatura || 0}%</span>
                            <span className="resumo-desc">Eficiência comercial</span>
                        </div>
                    </div>

                    <div className="ud-funil-modern-resumo-card resumo-3">
                        <div className="resumo-icon"><Icon name="payments" size={20} color="#fff" /></div>
                        <div className="resumo-info">
                            <span className="resumo-label">Fatura → Pagamento</span>
                            <span className="resumo-valor">{f.taxaConversaoFaturaPagamento || 0}%</span>
                            <span className="resumo-desc">Eficiência de cobrança</span>
                        </div>
                    </div>

                    <div className="ud-funil-modern-resumo-card resumo-4">
                        <div className="resumo-icon"><Icon name="local_shipping" size={20} color="#fff" /></div>
                        <div className="resumo-info">
                            <span className="resumo-label">Pagamento → Entrega</span>
                            <span className="resumo-valor">{f.taxaConversaoPagamentoEntrega || 0}%</span>
                            <span className="resumo-desc">Eficiência de entrega</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // 🔥 COMPONENTES MODERNOS — VISÃO GERAL
    // ==============================================
    const KPICardModerno = ({ icon, gradient, titulo, valor, subtitulo, detalhes }) => (
        <div className="ud-kpi-modern">
            <div className="ud-kpi-modern-header">
                <div className="ud-kpi-modern-icon" style={{ background: gradient }}>
                    <Icon name={icon} size={20} color="#fff" />
                </div>
                <span className="ud-kpi-modern-titulo">{titulo}</span>
            </div>
            <div className="ud-kpi-modern-valor">{valor}</div>
            {subtitulo && <div className="ud-kpi-modern-subtitulo">{subtitulo}</div>}
            {detalhes && detalhes.length > 0 && (
                <div className="ud-kpi-modern-detalhes">
                    {detalhes.map((d, i) => (
                        <div key={i} className="ud-kpi-modern-detalhe">
                            <span className="ud-kpi-modern-detalhe-dot" style={{ background: d.cor }} />
                            <span className="ud-kpi-modern-detalhe-label">{d.label}</span>
                            <strong className="ud-kpi-modern-detalhe-valor">{d.valor}</strong>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const SecaoModerna = ({ titulo, descricao, badge, children }) => (
        <div className="ud-secao-modern">
            <div className="ud-secao-modern-header">
                <div className="ud-secao-modern-title">
                    <h3>{titulo}</h3>
                    {descricao && <p>{descricao}</p>}
                </div>
                {badge && (
                    <div className="ud-secao-modern-badge">
                        {badge.icon && <Icon name={badge.icon} size={14} />}
                        <span>{badge.texto}</span>
                    </div>
                )}
            </div>
            <div className="ud-secao-modern-content">{children}</div>
        </div>
    );

    // ==============================================
    // 🔥 DASHBOARD MODAL
    // ==============================================
    const renderDashboardModal = () => {
        if (!showDashboardModal || !userForDashboard) return null;
        const u = dashboardDados?.utilizador || userForDashboard;

        return (
            <div className="ud-modal-overlay" onClick={closeDashboardModal}>
                <div className="ud-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ud-header">
                        <div className="ud-header-user">
                            <div className="ud-avatar">
                                {u?.imagemPerfil ? (
                                    <img src={getImagemUrl(u.imagemPerfil)} alt={u.nome} />
                                ) : (
                                    <span>{(u?.nome || '?').charAt(0).toUpperCase()}</span>
                                )}
                            </div>
                            <div className="ud-header-info">
                                <h2>{u?.nome}</h2>
                                <p>
                                    {u?.email} •
                                    <span className={`ud-perfil-badge ${(u?.perfil || 'Normal').toLowerCase()}`}>
                                        {u?.perfil}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="ud-header-actions">
                            <select
                                value={dashboardAno}
                                onChange={(e) => {
                                    const novoAno = parseInt(e.target.value);
                                    setDashboardAno(novoAno);
                                    carregarDashboard(userForDashboard._id, novoAno);
                                }}
                                className="ud-ano-select"
                            >
                                {[0, 1, 2, 3, 4].map(i => {
                                    const a = new Date().getFullYear() - i;
                                    return <option key={a} value={a}>{a}</option>;
                                })}
                            </select>
                            <button className="ud-close-btn" onClick={closeDashboardModal}>
                                <Icon name="close" size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="ud-tabs">
                        <button className={`ud-tab ${dashboardAba === 'visao' ? 'active' : ''}`} onClick={() => setDashboardAba('visao')}>
                            <Icon name="dashboard" size={16} /> Visão Geral
                        </button>
                        <button className={`ud-tab ${dashboardAba === 'funil' ? 'active' : ''}`} onClick={() => setDashboardAba('funil')}>
                            <Icon name="trending_up" size={16} /> Funil CRM
                        </button>
                        <button className={`ud-tab ${dashboardAba === 'detalhes' ? 'active' : ''}`} onClick={() => setDashboardAba('detalhes')}>
                            <Icon name="list_alt" size={16} /> Detalhes
                        </button>
                        <button className={`ud-tab ${dashboardAba === 'atividade' ? 'active' : ''}`} onClick={() => setDashboardAba('atividade')}>
                            <Icon name="history" size={16} /> Atividade
                        </button>
                    </div>

                    <div className="ud-body">
                        {dashboardLoading ? (
                            <div className="ud-loading">
                                <div className="ud-spinner"></div>
                                <p>A carregar estatísticas...</p>
                            </div>
                        ) : dashboardErro ? (
                            <div className="ud-erro">
                                <Icon name="error" size={40} color="#dc2626" />
                                <p>{dashboardErro}</p>
                                <button onClick={() => carregarDashboard(userForDashboard._id, dashboardAno)}>
                                    Tentar novamente
                                </button>
                            </div>
                        ) : dashboardDados ? (
                            <>
                                {dashboardAba === 'visao' && (
                                    <div className="ud-aba-content">
                                        <div className="ud-visao-header">
                                            <div className="ud-visao-header-text">
                                                <h2>Visão Geral do Utilizador</h2>
                                                <p>Resumo completo de atividade, documentos e conversões</p>
                                            </div>
                                            <div className="ud-visao-header-badge">
                                                <span className="ud-visao-header-badge-label">Ano</span>
                                                <span className="ud-visao-header-badge-valor">{dashboardAno}</span>
                                            </div>
                                        </div>

                                        <div className="ud-kpis-modern-grid">
                                            <KPICardModerno
                                                icon="description"
                                                gradient="linear-gradient(135deg, #4f46e5, #6366f1)"
                                                titulo="Cotações"
                                                valor={dashboardDados.kpis.cotacoes.total}
                                                subtitulo={formatarMoedaDashboard(dashboardDados.kpis.cotacoes.valorTotal)}
                                                detalhes={[
                                                    { label: 'Aprovadas', valor: dashboardDados.kpis.cotacoes.aprovadas, cor: '#10b981' },
                                                    { label: 'Pendentes', valor: dashboardDados.kpis.cotacoes.pendentes, cor: '#f59e0b' },
                                                    { label: 'Canceladas', valor: dashboardDados.kpis.cotacoes.canceladas, cor: '#ef4444' }
                                                ]}
                                            />
                                            <KPICardModerno
                                                icon="receipt_long"
                                                gradient="linear-gradient(135deg, #10b981, #34d399)"
                                                titulo="Faturas"
                                                valor={dashboardDados.kpis.faturas.total}
                                                subtitulo={formatarMoedaDashboard(dashboardDados.kpis.faturas.valorTotal)}
                                                detalhes={[
                                                    { label: 'Pagas', valor: dashboardDados.kpis.faturas.pagas, cor: '#10b981' },
                                                    { label: 'Pendentes', valor: dashboardDados.kpis.faturas.pendentes, cor: '#f59e0b' },
                                                    { label: 'Vencidas', valor: dashboardDados.kpis.faturas.vencidas, cor: '#ef4444' }
                                                ]}
                                            />
                                            <KPICardModerno icon="payments" gradient="linear-gradient(135deg, #f59e0b, #fbbf24)"
                                                titulo="Recibos" valor={dashboardDados.kpis.recibos.total}
                                                subtitulo={formatarMoedaDashboard(dashboardDados.kpis.recibos.valorTotal)} />
                                            <KPICardModerno icon="local_shipping" gradient="linear-gradient(135deg, #8b5cf6, #a78bfa)"
                                                titulo="Guias de Entrega" valor={dashboardDados.kpis.guias.total}
                                                subtitulo={`${dashboardDados.kpis.guias.entregues} entregues • ${dashboardDados.kpis.guias.parciais} parciais`} />
                                            <KPICardModerno icon="inventory_2" gradient="linear-gradient(135deg, #06b6d4, #22d3ee)"
                                                titulo="Produtos" valor={dashboardDados.kpis.produtos.total}
                                                subtitulo={`${dashboardDados.kpis.produtos.criados} criados • ${dashboardDados.kpis.produtos.atualizados} atualizados`} />
                                            <KPICardModerno icon="build" gradient="linear-gradient(135deg, #ec4899, #f472b6)"
                                                titulo="Serviços" valor={dashboardDados.kpis.servicos.total}
                                                subtitulo={`${dashboardDados.kpis.servicos.criados} criados • ${dashboardDados.kpis.servicos.atualizados} atualizados`} />
                                            <KPICardModerno icon="people" gradient="linear-gradient(135deg, #14b8a6, #2dd4bf)"
                                                titulo="Clientes Registados" valor={dashboardDados.kpis.clientes.total}
                                                subtitulo="Total de clientes" />
                                            <KPICardModerno icon="store" gradient="linear-gradient(135deg, #f97316, #fb923c)"
                                                titulo="Fornecedores" valor={dashboardDados.kpis.fornecedores.total}
                                                subtitulo="Total de fornecedores" />
                                            <KPICardModerno icon="shopping_cart" gradient="linear-gradient(135deg, #6366f1, #818cf8)"
                                                titulo="Compras" valor={dashboardDados.kpis.compras.total}
                                                subtitulo={formatarMoedaDashboard(dashboardDados.kpis.compras.valorTotal)} />
                                        </div>

                                        <SecaoModerna
                                            titulo="Análise Gráfica"
                                            descricao="Evolução mensal e distribuição de valores"
                                            badge={{ icon: 'trending_up', texto: 'Ano ' + dashboardAno }}
                                        >
                                            <div className="ud-charts-grid">
                                                <div className="ud-chart-card-modern">
                                                    <div className="ud-chart-card-modern-header">
                                                        <div className="ud-chart-card-modern-icon" style={{ background: 'linear-gradient(135deg, #4f46e5, #8b5cf6)' }}>
                                                            <Icon name="analytics" size={18} color="#fff" />
                                                        </div>
                                                        <div className="ud-chart-card-modern-title">
                                                            <h4>Evolução Mensal</h4>
                                                            <p>Documentos emitidos ao longo do ano</p>
                                                        </div>
                                                    </div>
                                                    <div className="ud-chart-card-modern-content">{renderGraficoEvolucao()}</div>
                                                </div>

                                                <div className="ud-chart-card-modern">
                                                    <div className="ud-chart-card-modern-header">
                                                        <div className="ud-chart-card-modern-icon" style={{ background: 'linear-gradient(135deg, #10b981, #34d399)' }}>
                                                            <Icon name="payments" size={18} color="#fff" />
                                                        </div>
                                                        <div className="ud-chart-card-modern-title">
                                                            <h4>Faturado vs Recebido</h4>
                                                            <p>Comparação mensal de valores</p>
                                                        </div>
                                                    </div>
                                                    <div className="ud-chart-card-modern-content">{renderGraficoValor()}</div>
                                                </div>

                                                <div className="ud-chart-card-modern">
                                                    <div className="ud-chart-card-modern-header">
                                                        <div className="ud-chart-card-modern-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}>
                                                            <Icon name="pie_chart" size={18} color="#fff" />
                                                        </div>
                                                        <div className="ud-chart-card-modern-title">
                                                            <h4>Distribuição por Módulo</h4>
                                                            <p>Proporção de documentos por tipo</p>
                                                        </div>
                                                    </div>
                                                    <div className="ud-chart-card-modern-content">{renderPizza()}</div>
                                                </div>

                                                <div className="ud-chart-card-modern">
                                                    <div className="ud-chart-card-modern-header">
                                                        <div className="ud-chart-card-modern-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }}>
                                                            <Icon name="emoji_events" size={18} color="#fff" />
                                                        </div>
                                                        <div className="ud-chart-card-modern-title">
                                                            <h4>Top Clientes Registados</h4>
                                                            <p>Clientes mais recentes neste ano</p>
                                                        </div>
                                                    </div>
                                                    <div className="ud-chart-card-modern-content">
                                                        {dashboardDados.topClientes?.length > 0 ? (
                                                            <div className="ud-top-lista-modern">
                                                                {dashboardDados.topClientes.map((c, i) => (
                                                                    <div key={i} className="ud-top-item-modern">
                                                                        <div className={`ud-top-rank-modern rank-${i + 1}`}>
                                                                            {i + 1}
                                                                        </div>
                                                                        <div className="ud-top-info-modern">
                                                                            <strong>{c.nome}</strong>
                                                                            <small>{c.email || c.telefone || c.cidade || '-'}</small>
                                                                        </div>
                                                                        <small className="ud-top-data-modern">
                                                                            {formatarDataDashboard(c.dataCriacao)}
                                                                        </small>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="ud-empty-modern">
                                                                <Icon name="info" size={32} color="#9ca3af" />
                                                                <p>Sem clientes registados neste ano</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </SecaoModerna>
                                    </div>
                                )}

                                {dashboardAba === 'funil' && (
                                    <div className="ud-aba-content">{renderFunil()}</div>
                                )}

                                {dashboardAba === 'detalhes' && (
                                    <div className="ud-aba-content">
                                        <div className="ud-detalhes-grid">
                                            <div className="ud-detalhe-bloco">
                                                <h4><Icon name="description" size={18} /> Cotações por Status</h4>
                                                {Object.entries(dashboardDados.kpis.cotacoes.porStatus || {}).map(([status, count]) => (
                                                    <div key={status} className="ud-detalhe-linha">
                                                        <span>{status}</span><strong>{count}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="ud-detalhe-bloco">
                                                <h4><Icon name="receipt_long" size={18} /> Faturas por Status</h4>
                                                {Object.entries(dashboardDados.kpis.faturas.porStatus || {}).map(([status, count]) => (
                                                    <div key={status} className="ud-detalhe-linha">
                                                        <span>{status}</span><strong>{count}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="ud-detalhe-bloco">
                                                <h4><Icon name="payments" size={18} /> Recibos por Método</h4>
                                                {Object.entries(dashboardDados.kpis.recibos.porMetodo || {}).map(([metodo, count]) => (
                                                    <div key={metodo} className="ud-detalhe-linha">
                                                        <span>{metodo}</span><strong>{count}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="ud-detalhe-bloco">
                                                <h4><Icon name="local_shipping" size={18} /> Guias de Entrega</h4>
                                                <div className="ud-detalhe-linha"><span>Emitidas</span><strong>{dashboardDados.kpis.guias.emitidas}</strong></div>
                                                <div className="ud-detalhe-linha"><span>Entregues</span><strong>{dashboardDados.kpis.guias.entregues}</strong></div>
                                                <div className="ud-detalhe-linha"><span>Parciais</span><strong>{dashboardDados.kpis.guias.parciais}</strong></div>
                                                <div className="ud-detalhe-linha"><span>Anuladas</span><strong>{dashboardDados.kpis.guias.anuladas}</strong></div>
                                            </div>
                                            <div className="ud-detalhe-bloco">
                                                <h4><Icon name="shopping_cart" size={18} /> Compras a Fornecedores</h4>
                                                <div className="ud-detalhe-linha"><span>Total</span><strong>{dashboardDados.kpis.compras.total}</strong></div>
                                                <div className="ud-detalhe-linha"><span>Pagas</span><strong>{dashboardDados.kpis.compras.pagas}</strong></div>
                                                <div className="ud-detalhe-linha"><span>Pendentes</span><strong>{dashboardDados.kpis.compras.pendentes}</strong></div>
                                                <div className="ud-detalhe-linha"><span>Valor</span><strong>{formatarMoedaDashboard(dashboardDados.kpis.compras.valorTotal)}</strong></div>
                                            </div>
                                            <div className="ud-detalhe-bloco">
                                                <h4><Icon name="store" size={18} /> Top Fornecedores</h4>
                                                {dashboardDados.topFornecedores?.length > 0 ? (
                                                    dashboardDados.topFornecedores.map((f, i) => (
                                                        <div key={i} className="ud-detalhe-linha">
                                                            <span>{f.nome}</span>
                                                            <small>{f.cidade || f.email || '-'}</small>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="ud-empty-small">Sem fornecedores neste ano</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {dashboardAba === 'atividade' && (
                                    <div className="ud-aba-content">
                                        <h3>🕐 Atividade Recente</h3>
                                        {dashboardDados.atividadeRecente?.length > 0 ? (
                                            <div className="ud-timeline">
                                                {dashboardDados.atividadeRecente.map((log, i) => (
                                                    <div key={i} className="ud-timeline-item">
                                                        <div className="ud-timeline-dot"></div>
                                                        <div className="ud-timeline-content">
                                                            <div className="ud-timeline-acao">{log.acao}</div>
                                                            <div className="ud-timeline-meta">
                                                                <span className="ud-timeline-modulo">{log.modulo}</span>
                                                                <span className="ud-timeline-data">{formatarDataHoraDashboard(log.dataHora)}</span>
                                                            </div>
                                                            {log.detalhes && <div className="ud-timeline-detalhes">{log.detalhes}</div>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="ud-empty">Sem atividade registada</div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER — TABS DO MODAL DE EDIÇÃO
    // ==============================================
    const renderModalTabs = () => (
        <div className="modal-tabs-container">
            <div className="modal-tabs">
                <button className={`modal-tab ${activeTab === 'dados' ? 'active' : ''}`} onClick={() => setActiveTab('dados')}>
                    <Icon name="person" size={16} /> <span>Dados</span>
                </button>
                <button className={`modal-tab ${activeTab === 'permissoes' ? 'active' : ''}`} onClick={() => setActiveTab('permissoes')}>
                    <Icon name="security" size={16} /> <span>Permissões Individuais</span>
                    {temPermissoesIndividuais() && <span className="tab-badge">•</span>}
                </button>
            </div>
        </div>
    );

    // ==============================================
    // RENDER — TOAST
    // ==============================================
    const renderToast = () => {
        if (!tempToast) return null;
        return (
            <div className={`utilizadores-toast ${tempToast.type}`}>
                <div className="toast-icon">
                    <Icon name={tempToast.type === 'success' ? 'add' : 'close'} size={20} />
                </div>
                <div className="toast-content">
                    <div className="toast-title">{tempToast.title}</div>
                    <div className="toast-message">{tempToast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setTempToast(null)}>×</button>
            </div>
        );
    };

    // ==============================================
    // RENDER — PAGINAÇÃO
    // ==============================================
    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const startItem = (currentPage - 1) * itemsPerPage + 1;
        const endItem = Math.min(currentPage * itemsPerPage, filteredUsers.length);

        return (
            <div className="utilizadores-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {filteredUsers.length} utilizadores</span>
                </div>
                <div className="pagination-controls">
                    <button className="pagination-btn" onClick={goToPreviousPage} disabled={currentPage === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                                return (
                                    <button key={page} className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => goToPage(page)}>{page}</button>
                                );
                            }
                            if ((page === currentPage - 3 && page > 2) || (page === currentPage + 3 && page < totalPages - 1)) {
                                return <span key={page} className="pagination-ellipsis">…</span>;
                            }
                            return null;
                        })}
                    </div>
                    <button className="pagination-btn" onClick={goToNextPage} disabled={currentPage === totalPages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER — PERMISSÕES INDIVIDUAIS
    // ==============================================
    const renderPermissoesIndividuais = () => {
        const permissoes = formData.permissoesIndividuais || {};
        const modulos = getModulosComPermissoes(formData.perfil);

        const modulosFiltrados = {};
        Object.keys(modulos).forEach(moduloId => {
            if (isModuloAtivoNaEmpresa(moduloId)) {
                const modulo = modulos[moduloId];
                const submodulosFiltrados = {};
                Object.keys(modulo.submodulos).forEach(subId => {
                    if (isSubmoduloAtivoNaEmpresa(moduloId, subId)) {
                        submodulosFiltrados[subId] = modulo.submodulos[subId];
                    }
                });
                if (Object.keys(submodulosFiltrados).length > 0) {
                    modulosFiltrados[moduloId] = {
                        label: modulo.label, icon: modulo.icon, ativo: modulo.ativo,
                        submodulos: submodulosFiltrados
                    };
                }
            }
        });

        if (Object.keys(modulosFiltrados).length === 0) {
            return (
                <div className="utilizadores-permissoes-container">
                    <div className="utilizadores-permissoes-empty">
                        <Icon name="info" size={32} color="var(--color-info-dark)" />
                        <h4>Nenhuma permissão disponível</h4>
                        <p>A empresa não possui módulos ativos ou o utilizador não tem permissões atribuídas.</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="utilizadores-permissoes-container">
                <div className="utilizadores-permissoes-header">
                    <div className="utilizadores-permissoes-header-info">
                        <h4>Permissões Individuais para <strong>{formData.perfil}</strong></h4>
                        <p>Personalize o acesso do utilizador a cada módulo e submódulo ativo na empresa.</p>
                        {!isSuperadmin && formData.empresaId && (
                            <small style={{ color: 'var(--color-info-dark)', display: 'block', marginTop: '0.2rem' }}>
                                🔒 Apenas módulos ativos da empresa são exibidos
                            </small>
                        )}
                    </div>
                    <button className="utilizadores-btn-reset-permissoes" onClick={resetarPermissoesParaBase}>
                        <Icon name="refresh" size={16} /> Redefinir
                    </button>
                </div>

                <div className="utilizadores-permissoes-grid">
                    {Object.keys(modulosFiltrados).map(moduloId => {
                        const modulo = modulosFiltrados[moduloId];
                        const permissoesModulo = permissoes[moduloId] || { ativo: false, submodulos: {} };
                        const hasSubmodulos = Object.keys(modulo.submodulos).length > 0;

                        return (
                            <div key={moduloId} className="utilizadores-modulo-permissao-card">
                                <div className="utilizadores-modulo-permissao-header">
                                    <div className="utilizadores-modulo-permissao-info">
                                        <Icon name={modulo.icon} size={18} />
                                        <span className="utilizadores-modulo-permissao-nome">{modulo.label}</span>
                                    </div>
                                    <label className="utilizadores-toggle-permissao">
                                        <input type="checkbox" checked={permissoesModulo.ativo || false}
                                            onChange={() => togglePermissaoIndividual(moduloId)} />
                                        <span className="utilizadores-toggle-slider"></span>
                                        <span className="utilizadores-toggle-label">
                                            {permissoesModulo.ativo ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </label>
                                </div>

                                {hasSubmodulos && permissoesModulo.ativo && (
                                    <div className="utilizadores-submodulos-permissao-grid">
                                        {Object.keys(modulo.submodulos).map(subId => {
                                            const sub = modulo.submodulos[subId];
                                            const subAtivo = permissoesModulo.submodulos[subId] || false;
                                            return (
                                                <label key={subId} className="utilizadores-submodulo-permissao-item">
                                                    <input type="checkbox" checked={subAtivo}
                                                        onChange={() => togglePermissaoIndividual(moduloId, subId)} />
                                                    <span className="utilizadores-submodulo-permissao-indicator"></span>
                                                    <span className="utilizadores-submodulo-permissao-label">{sub.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}

                                {hasSubmodulos && !permissoesModulo.ativo && (
                                    <div className="utilizadores-submodulos-desativados">
                                        <span className="utilizadores-submodulos-desativados-text">
                                            <Icon name="lock" size={14} /> Módulo desativado - submódulos bloqueados
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="utilizadores-permissoes-footer">
                    <div className="utilizadores-permissoes-status">
                        {temPermissoesIndividuais() ? (
                            <span className="utilizadores-permissoes-custom">
                                <Icon name="check_circle" size={16} /> Permissões personalizadas ativas
                            </span>
                        ) : (
                            <span className="utilizadores-permissoes-default">
                                <Icon name="check" size={16} /> Usando permissões padrão do perfil
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER — MODAL DE EDIÇÃO/ADIÇÃO
    // ==============================================
    const renderModal = () => {
        if (!showModal) return null;
        const currentUser = obterUtilizadorLogado(loggedUser);
        const perfilLogado = normalizarPerfil(currentUser);
        const isAdmin = perfilLogado === 'admin';
        const isSuper = perfilLogado === 'superadmin';

        if (isAdmin && !isEditing && !formData.empresaId) {
            const empresaAdminId = obterEmpresaIdDoUtilizador(currentUser);
            if (empresaAdminId) {
                const empresa = empresasList.find(e => e._id === empresaAdminId);
                if (empresa) {
                    formData.empresaId = empresaAdminId;
                    formData.empresaNome = empresa.nome || '';
                }
            }
        }

        const limiteMsg = !isSuper && !isEditing ? getLimiteMessage() : null;
        const selectHabilitado = isSuper;

        return (
            <div className="utilizadores-modal-overlay" onClick={closeModal}>
                <div className="utilizadores-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="utilizadores-modal-header">
                        <h3>{isEditing ? 'Editar Utilizador' : 'Adicionar Utilizador'}</h3>
                        <button className="utilizadores-modal-close" onClick={closeModal}>×</button>
                    </div>

                    {renderModalTabs()}

                    <div className="utilizadores-modal-body">
                        {limiteMsg && (
                            <div className="utilizadores-limite-msg">
                                <Icon name={limiteUtilizadores.disponivel <= 0 ? 'warning' : 'info'} size={16} />
                                <span>{limiteMsg}</span>
                            </div>
                        )}

                        {activeTab === 'dados' ? (
                            <>
                                <div className="utilizadores-form-group">
                                    <label>Imagem de Perfil</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                        <div style={{
                                            width: '80px', height: '80px', borderRadius: '50%',
                                            backgroundColor: 'var(--color-light)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', border: '2px solid var(--color-light)'
                                        }}>
                                            {imagePreview ? (
                                                <img src={imagePreview} alt="Preview"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : isEditing && selectedUser?.imagemPerfil ? (
                                                <img src={getImagemUrl(selectedUser.imagemPerfil)} alt="Perfil atual"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Icon name="customers" size={40} color="var(--color-info-dark)" />
                                            )}
                                        </div>
                                        <div>
                                            <input type="file" ref={fileInputRef} accept="image/*"
                                                style={{ display: 'none' }} onChange={handleImageChange} />
                                            <button type="button" className="utilizadores-btn-imagem"
                                                onClick={() => fileInputRef.current?.click()}>
                                                {isEditing ? 'Alterar Imagem' : 'Selecionar Imagem'}
                                            </button>
                                            {imagePreview && (
                                                <button type="button" className="utilizadores-btn-remover-imagem"
                                                    onClick={removeSelectedImage}>Remover</button>
                                            )}
                                        </div>
                                    </div>
                                    <small className="utilizadores-form-hint">Formatos aceitos: JPG, PNG, GIF. Máximo 5MB</small>
                                </div>

                                <div className="utilizadores-form-group">
                                    <label>Nome completo *</label>
                                    <input type="text" value={formData.nome}
                                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                        placeholder="Ex: João Silva" />
                                </div>
                                <div className="utilizadores-form-group">
                                    <label>Email *</label>
                                    <input type="email" value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="exemplo@dominio.com" />
                                </div>

                                <div className="utilizadores-form-group">
                                    <label>{isEditing ? 'Nova Senha (opcional)' : 'Senha *'}</label>
                                    <input type="password" value={formData.senha}
                                        onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                                        placeholder={isEditing ? 'Deixe em branco para manter a atual' : 'Mínimo 6 caracteres'} />
                                    {isEditing && <small>Preencha apenas se desejar alterar a senha</small>}
                                    {!isEditing && <small>A senha deve ter no mínimo 6 caracteres</small>}
                                </div>

                                <div className="utilizadores-perfil-empresa-grid">
                                    <div className="utilizadores-form-group utilizadores-perfil-empresa-item">
                                        <label>Perfil</label>
                                        <select className="utilizadores-perfil-select" value={formData.perfil}
                                            onChange={(e) => setFormData({ ...formData, perfil: e.target.value })}
                                            disabled={!isSuper && !isAdmin}>
                                            {isSuper ? (
                                                <>
                                                    <option value="Normal">Normal</option>
                                                    <option value="Contabilista">Contabilista</option>
                                                    <option value="Comercial">Comercial</option>
                                                    <option value="Admin">Admin</option>
                                                    <option value="Superadmin">Superadmin</option>
                                                </>
                                            ) : isAdmin ? (
                                                <>
                                                    <option value="Normal">Normal</option>
                                                    <option value="Contabilista">Contabilista</option>
                                                    <option value="Comercial">Comercial</option>
                                                </>
                                            ) : (
                                                <option value={selectedUser?.perfil || 'Normal'}>
                                                    {selectedUser?.perfil || 'Normal'}
                                                </option>
                                            )}
                                        </select>
                                        {isAdmin && <small>Admin só pode atribuir perfis: Normal, Contabilista ou Comercial</small>}
                                    </div>

                                    <div className="utilizadores-form-group utilizadores-perfil-empresa-item">
                                        <label>Empresa</label>
                                        <select className="utilizadores-empresa-select" value={formData.empresaId || ''}
                                            onChange={(e) => {
                                                if (!isSuper && !isAdmin) return;
                                                const empresaId = e.target.value;
                                                const empresa = empresasList.find(emp => emp._id === empresaId);
                                                setFormData({ ...formData, empresaId, empresaNome: empresa?.nome || '' });
                                            }}
                                            disabled={!selectHabilitado && !isAdmin}>
                                            <option value="">Sem empresa</option>
                                            {isSuper ? (
                                                empresasList.map(emp => (
                                                    <option key={emp._id} value={emp._id}>{emp.nome}</option>
                                                ))
                                            ) : isAdmin ? (
                                                (() => {
                                                    const empresaAdminId = obterEmpresaIdDoUtilizador(currentUser);
                                                    const empresa = empresasList.find(e => e._id === empresaAdminId);
                                                    if (empresa) return <option key={empresa._id} value={empresa._id}>{empresa.nome}</option>;
                                                    return null;
                                                })()
                                            ) : (
                                                formData.empresaId && (
                                                    <option value={formData.empresaId}>{formData.empresaNome || 'Empresa atual'}</option>
                                                )
                                            )}
                                        </select>
                                        {isSuper && <small>Pode atribuir o utilizador a qualquer empresa</small>}
                                        {isAdmin && <small>Admin só pode adicionar utilizadores à sua própria empresa</small>}
                                    </div>
                                </div>

                                <div className="utilizadores-form-group">
                                    <label>Status</label>
                                    <select value={formData.ativo ? 'ativo' : 'inativo'}
                                        onChange={(e) => setFormData({ ...formData, ativo: e.target.value === 'ativo' })}
                                        disabled={!isSuper && !isAdmin}>
                                        <option value="ativo">Ativo</option>
                                        <option value="inativo">Inativo</option>
                                    </select>
                                </div>
                            </>
                        ) : (
                            renderPermissoesIndividuais()
                        )}
                    </div>

                    <div className="utilizadores-modal-footer">
                        <button className="utilizadores-btn-cancel" onClick={closeModal} disabled={isUploadingImage}>
                            Cancelar
                        </button>
                        <button className="utilizadores-btn-save" onClick={saveUser} disabled={isUploadingImage}>
                            {isUploadingImage ? 'A enviar imagem...' : (isEditing ? 'Atualizar' : 'Adicionar')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // 🔥 RENDER — MODAL DELETE
    // ==============================================
    const renderDeleteConfirm = () => {
        if (!showDeleteConfirm || !userToDelete) return null;
        const inativo = userToDelete.ativo === false;

        return (
            <div className="utilizadores-delete-confirm-overlay" onClick={closeDeleteConfirm}>
                <div className="utilizadores-delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="utilizadores-delete-confirm-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#ff3b30" />
                        </svg>
                    </div>
                    <h3>Apagar Utilizador Definitivamente</h3>
                    <p>
                        Tem certeza que deseja <strong>apagar permanentemente</strong> o utilizador<br />
                        <strong>"{userToDelete.nome}"</strong>?
                    </p>
                    <p className="utilizadores-delete-confirm-warning">
                        ⚠️ Esta ação remove o utilizador do sistema e não pode ser desfeita.
                    </p>
                    {!inativo && (
                        <p style={{ color: '#FF9500', fontWeight: 600, marginTop: '0.6rem', fontSize: '0.85rem' }}>
                            🔒 O utilizador está ATIVO. Desabilite-o primeiro para poder apagar.
                        </p>
                    )}
                    <div className="utilizadores-delete-confirm-buttons">
                        <button className="utilizadores-delete-confirm-cancel" onClick={closeDeleteConfirm} disabled={deletingUser}>
                            Cancelar
                        </button>
                        <button
                            className="utilizadores-delete-confirm-confirm"
                            onClick={deleteUser}
                            disabled={deletingUser || !inativo}
                            title={!inativo ? 'Desabilite o utilizador primeiro' : 'Apagar definitivamente'}
                            style={{
                                opacity: !inativo ? 0.5 : 1,
                                cursor: !inativo ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {deletingUser ? 'A apagar...' : 'Apagar Definitivamente'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    const currentPageUsers = getCurrentPageUsers();

    if (!sessaoCarregada) {
        return (
            <div className="utilizadores-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>A carregar sessão...</p>
                </div>
            </div>
        );
    }

    // 🔥 LOG de diagnóstico (aparece apenas 1x no console)
    if (typeof window !== 'undefined' && !window.__debugLogado) {
        window.__debugLogado = true;
        console.log('🔍 [RENDER] Perfil atual:', loggedUserPerfil, '| ID:', loggedUserId, '| isSuperadmin:', isSuperadmin);
    }

    return (
        <div className="utilizadores-container">
            <div className="utilizadores-header">
                <div className="header-title">
                    <h1>👥 Gestão de Utilizadores</h1>
                    <p>Gerencie todos os utilizadores do sistema</p>
                </div>
                <button className="btn-add" onClick={openAddModal}>
                    <Icon name="add" size={20} /> Novo Utilizador
                </button>
            </div>

            <div className="utilizadores-search">
                <div className="search-wrapper">
                    <Icon name="search" size={20} color="var(--color-info-dark)" />
                    <input type="text" placeholder="Pesquisar por nome, email ou empresa..."
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')} type="button">×</button>
                    )}
                </div>
            </div>

            <div className="utilizadores-stats">
                <div className="stat-card"><div className="stat-number">{stats.total}</div><div className="stat-label">TOTAL</div></div>
                <div className="stat-card success"><div className="stat-number">{stats.ativos}</div><div className="stat-label">ATIVOS</div></div>
                <div className="stat-card danger"><div className="stat-number">{stats.inativos}</div><div className="stat-label">INATIVOS</div></div>
                <div className="stat-card primary"><div className="stat-number">{stats.admins}</div><div className="stat-label">ADMINS</div></div>
            </div>

            <div className="utilizadores-table-wrapper">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>A carregar utilizadores...</p>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="empty-state">
                        <Icon name="customers" size={48} color="var(--color-info-dark)" />
                        <h3>Nenhum utilizador encontrado</h3>
                        <p>{searchTerm ? 'Tente ajustar os filtros de pesquisa' : 'Adicione o primeiro utilizador'}</p>
                    </div>
                ) : (
                    <>
                        <table className="utilizadores-table">
                            <thead>
                                <tr>
                                    <th>UTILIZADOR</th>
                                    <th>CONTACTO</th>
                                    <th>PERFIL</th>
                                    <th>EMPRESA</th>
                                    <th>STATUS</th>
                                    <th>CRIAÇÃO</th>
                                    <th>AÇÕES</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPageUsers.map((user, index) => {
                                    const canEdit = podeEditar(user);
                                    const canDelete = podeApagar(user);
                                    const canToggle = podeAlternarEstado(user);
                                    const podeApagarAgora = podeApagarDefinitivamente(user);

                                    let imagemUrl = null;
                                    if (user.imagemPerfil) imagemUrl = getImagemUrl(user.imagemPerfil);

                                    return (
                                        <tr key={user._id || index}>
                                            <td>
                                                <div className="user-info">
                                                    <Avatar name={user.nome} src={imagemUrl} size={36} />
                                                    <div className="user-details">
                                                        <span className="user-name">{user.nome}</span>
                                                        <span className="user-email">{user.email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="contact-info">
                                                    <span className="contact-email">{user.email}</span>
                                                    {user.telefone && <span className="contact-phone">{user.telefone}</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`perfil-badge ${user.perfil?.toLowerCase()}`}>
                                                    {user.perfil || 'Normal'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="empresa-cell">
                                                    {user.empresaNome || user.empresa || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${user.ativo !== false ? 'ativo' : 'inativo'}`}>
                                                    {user.ativo !== false ? 'ATIVO' : 'INATIVO'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="criacao-cell">
                                                    {user.dataCriacao ? new Date(user.dataCriacao).toLocaleDateString('pt-PT') : '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="acoes-cell">
                                                    <button
                                                        className="action-btn dashboard"
                                                        onClick={() => openDashboardModal(user)}
                                                        title="Ver detalhes e estatísticas"
                                                        aria-label="Ver detalhes"
                                                    >
                                                        <Icon name="analytics" size={16} />
                                                    </button>

                                                    {canEdit && (
                                                        <button className="action-btn edit" onClick={() => openEditModal(user)}
                                                            title="Editar utilizador" aria-label="Editar">
                                                            <Icon name="edit" size={16} />
                                                        </button>
                                                    )}

                                                    {canToggle && (
                                                        <button
                                                            className={`action-btn toggle ${user.ativo !== false ? '' : 'inativo'}`}
                                                            onClick={() => toggleUserStatus(user)}
                                                            title={user.ativo !== false ? 'Bloquear utilizador' : 'Desbloquear utilizador'}
                                                            
                                                        >
                                                            <Icon name={user.ativo !== false ? 'lock_open' : 'lock'} size={16} color="#fff" />
                                                        </button>
                                                    )}

                                                    {canDelete && (
                                                        <button
                                                            className="action-btn delete"
                                                            onClick={() => confirmDelete(user)}
                                                            title={
                                                                podeApagarAgora
                                                                    ? 'Apagar definitivamente'
                                                                    : '🔒 Desabilite o utilizador primeiro'
                                                            }
                                                            disabled={deletingUser || !podeApagarAgora}
                                                            style={{
                                                                opacity: podeApagarAgora ? 1 : 0.35,
                                                                cursor: podeApagarAgora ? 'pointer' : 'not-allowed'
                                                            }}
                                                        >
                                                            <Icon name="delete" size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {renderPagination()}
                    </>
                )}
            </div>

            {renderModal()}
            {renderDeleteConfirm()}
            {renderToast()}
            {renderDashboardModal()}
        </div>
    );
}